/* eslint-disable jsx-a11y/media-has-caption */
import { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { ImageViewer, Spinner } from '@components';
import { Answer, AnswerSolution, QuestionType } from '@types';

import MCQ from '@features/homeworks/components/mcq/MCQ';
import MSQ from '@features/homeworks/components/msq/MSQ';
import FillInTheBlanks from '@features/homeworks/components/fill-in-the-blanks/FillInTheBlanks';
import ShortAndLongAnswer from '@features/homeworks/components/short-and-long-answer/ShortAndLongAnswer';
import safeParseHtml from '@utils/safeParseHtml';
import { useLazyGetQuestionPreviewQuery } from '../queries/homeWorkQueries';
import AnswerInBlanks from '../components/answer-in-blanks/AnswerInBlanks';

const QuestionPreview = () => {
  const { questionId = '' } = useParams();
  const [searchParams] = useSearchParams();

  console.log('hitted', searchParams);

  const previewToken = searchParams.get('previewToken') || '';

  const [getQuestionPreview, { data, isLoading, isError }] =
    useLazyGetQuestionPreviewQuery();

  useEffect(() => {
    if (questionId && previewToken) {
      getQuestionPreview({
        questionId,
        previewToken
      });
    }
  }, [questionId, previewToken, getQuestionPreview]);

  const question = data?.data;

  /**
   * Keep step-by-step solutions in API order.
   */
  const orderedAnswerSolutions = useMemo(
    () =>
      [...(question?.answerSolutions ?? [])].sort(
        (a: AnswerSolution, b: AnswerSolution) => a.orderNo - b.orderNo
      ),
    [question?.answerSolutions]
  );

  /**
   * For MCQ/MSQ only correct answers are displayed.
   * For other question types, answers themselves are the solution.
   */
  const correctAnswer =
    question?.type === QuestionType.MCQ || question?.type === QuestionType.MSQ
      ? question?.answers?.filter((item: Answer) => item?.isCorrect) ?? []
      : question?.answers ?? [];

  /**
   * Existing student question components.
   */
  const renderQuestion = () => {
    switch (question?.type) {
      case QuestionType.MCQ:
        return (
          <MCQ
            question={question}
            onInputChange={() => {}}
            selectedAnswer=""
            disableInput
          />
        );

      case QuestionType.MSQ:
        return (
          <MSQ
            question={question}
            onInputChange={() => {}}
            selectedAnswer={[]}
            disableInput
          />
        );

      case QuestionType.FILL_IN_THE_BLANKS:
        return <FillInTheBlanks question={question} onInputChange={() => {}} />;

      case QuestionType.SHORT_ANSWER:
      case QuestionType.LONG_ANSWER:
        return (
          <ShortAndLongAnswer
            question={question}
            onInputChange={() => {}}
            uploadedFiles={[]}
            setUploadedFiles={() => {}}
            parentRef={{ current: undefined }}
            disableInput
            answer=""
            isCanvasRequired={false}
          />
        );

      default:
        return null;
    }
  };

  /**
   * Answer attachment.
   */
  const renderAnswerAttachment = (answer: Answer) => {
    if (!answer?.answerAttachments?.length) {
      return null;
    }

    return (
      <div className="mt-3 flex flex-wrap gap-4">
        {answer.answerAttachments.map((attachment: any) => (
          <ImageViewer
            key={attachment?.id}
            src={attachment?.attachments?.url}
            isExpandable
            alt="answer solution"
            className="w-40"
          />
        ))}
      </div>
    );
  };

  /**
   * Single answer.
   */
  const showSingleSolutionAndAnswer = (answer: Answer) => (
    <div key={answer?.id} className="text-base text-content">
      {answer?.answer ? safeParseHtml(answer.answer) : '--'}

      {renderAnswerAttachment(answer)}
    </div>
  );

  /**
   * Multiple answers.
   */
  const showMultiSolutionAndAnswer = (answer: Answer, answerIndex: number) => (
    <div
      key={answer?.id}
      className="flex items-start gap-3 text-base text-content"
    >
      <span className="font-semibold text-primary">{answerIndex}.</span>

      <div>
        <div>{answer?.answer ? safeParseHtml(answer.answer) : '--'}</div>

        {renderAnswerAttachment(answer)}
      </div>
    </div>
  );

  /**
   * Render solution.
   */
  const renderSolution = () => {
    if (question?.type === QuestionType.FILL_IN_THE_BLANKS) {
      return <AnswerInBlanks answer={question?.answers?.[0]?.answer ?? ''} />;
    }

    if (correctAnswer?.length > 0) {
      if (correctAnswer.length === 1) {
        return correctAnswer.map((answer: Answer) =>
          showSingleSolutionAndAnswer(answer)
        );
      }

      return correctAnswer.map((answer: Answer, index: number) =>
        showMultiSolutionAndAnswer(answer, index + 1)
      );
    }

    return null;
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!previewToken) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="rounded-xl border border-red-200 bg-red-50 px-6 py-4 text-red-600">
          Invalid preview link
        </div>
      </div>
    );
  }

  if (isError || !question) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="rounded-xl border border-red-200 bg-red-50 px-6 py-4 text-red-600">
          Unable to load question preview
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-y-auto bg-[#F5F5F5]">
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-5xl">
          {/* Page Header */}

          {/* Main Card */}
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-[#FCFBF7] shadow-xl">
            {/* Card Header */}
            <div className="flex items-center justify-between border-b bg-white/70 px-5 py-4 sm:px-8">
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  Question
                </div>

                <div className="mt-1 text-sm font-semibold text-content">
                  #{questionId}
                </div>
              </div>

              <div className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-600">
                Preview
              </div>
            </div>

            {/* Content */}
            <div className="px-5 py-7 sm:px-8 lg:px-10 whitespace-pre-line">
              {/* ================= QUESTION ================= */}
              <section>
                {/* Reuse existing question component */}
                {renderQuestion()}

                {/* Question attachments */}
              </section>

              {/* ================= SOLUTION ================= */}
              {question?.type !== QuestionType.AUDIO_INPUT &&
                correctAnswer?.length > 0 && (
                  <section className="mt-10 border-t border-gray-200 pt-8">
                    <div className="mb-5 flex items-center gap-3">
                      <div className="h-7 w-1 rounded-full bg-primary" />

                      <h2 className="text-lg font-bold text-primary">
                        Solution
                      </h2>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-5">
                      {renderSolution()}
                    </div>
                  </section>
                )}

              {/* ============ STEP-BY-STEP SOLUTION ============ */}
              {orderedAnswerSolutions.length > 0 && (
                <section className="mt-10 border-t border-gray-200 pt-8">
                  <div className="mb-5 flex items-center gap-3">
                    <div className="h-7 w-1 rounded-full bg-primary" />

                    <h2 className="text-lg font-bold text-primary">
                      Step-by-Step Solution
                    </h2>
                  </div>

                  <div className="space-y-4">
                    {orderedAnswerSolutions.map(
                      (solution: AnswerSolution, index: number) => (
                        <div
                          key={solution.id}
                          className="rounded-xl border border-gray-200 bg-white p-5"
                        >
                          <div className="mb-4 flex items-center gap-3">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">
                              {index + 1}
                            </div>

                            <div className="text-sm font-semibold text-primary">
                              Step {index + 1}
                            </div>
                          </div>

                          {solution.solution && (
                            <div className="ml-11 text-base leading-7 text-content">
                              {safeParseHtml(solution.solution)}
                            </div>
                          )}

                          {solution.attachments?.url && (
                            <div className="ml-11 mt-5">
                              <ImageViewer
                                src={solution.attachments.url}
                                isExpandable
                                alt={solution.attachments.name || 'Solution'}
                                className="w-3/5 mobile:w-5/6"
                              />
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionPreview;
